import storefront from "./index";

import { PRODUCT_CARD_GROUPING_NODES_QUERY, PRODUCT_SWATCH_COLOR_PATTERNS_QUERY } from "./queries";

const groupCache = new Map();
const productCache = new Map();

export function unique(values) {
  return [...new Set(values.filter(Boolean))];
}

function chunk(items, size = 250) {
  const chunks = [];

  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }

  return chunks;
}

function getReferenceProductIds(field) {
  return field?.references?.nodes?.map((node) => node?.id).filter(Boolean) || [];
}

export function collectGroupBuckets(node, buckets) {
  if (!node) {
    return;
  }

  const solids = getReferenceProductIds(node.solids);
  const florals = getReferenceProductIds(node.florals);

  if (solids.length) {
    buckets.set(`${node.id}::solids`, { type: "solids", productIds: unique(solids) });
  }

  if (florals.length) {
    buckets.set(`${node.id}::florals`, { type: "florals", productIds: unique(florals) });
  }

  const options = node.options?.references?.nodes || [];

  options.forEach((option) => {
    collectGroupBuckets(option, buckets);
  });
}

export function findColorFamilyBucket(buckets, currentProductId) {
  for (const bucket of buckets.values()) {
    if (bucket.productIds.includes(currentProductId)) {
      return bucket;
    }
  }

  return null;
}

export function getColorPattern(product) {
  return product?.colorPattern?.references?.nodes?.[0] || null;
}

export function getSwatchLabel(product) {
  const colorPattern = getColorPattern(product);

  return colorPattern?.swatchLabel?.value || product?.title?.split("|")?.[1]?.trim() || product?.title || "";
}

export function getSwatchColor(product) {
  return getColorPattern(product)?.swatchColor?.value || "";
}

export function getSwatchImage(product) {
  return getColorPattern(product)?.swatchImage?.reference?.image?.url || "";
}

export function getColorPatternLabel(product) {
  return getColorPattern(product)?.swatchLabel?.value || "";
}

export function getProductBadges(product) {
  try {
    const parsed = product?.badges?.value ? JSON.parse(product.badges.value) : [];

    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
}

export async function fetchProductGroups(groupIds) {
  const missingIds = groupIds.filter((id) => !groupCache.has(id));

  for (const ids of chunk(missingIds)) {
    const response = await storefront.request({ query: PRODUCT_CARD_GROUPING_NODES_QUERY, variables: { ids } });

    response?.data?.nodes?.forEach((group) => {
      if (group?.id) {
        groupCache.set(group.id, group);
      }
    });
  }

  return groupIds.map((id) => groupCache.get(id)).filter(Boolean);
}

export async function fetchProducts(productIds) {
  const missingIds = productIds.filter((id) => !productCache.has(id));

  for (const ids of chunk(missingIds)) {
    const response = await storefront.request({ query: PRODUCT_SWATCH_COLOR_PATTERNS_QUERY, variables: { ids } });

    response?.data?.nodes?.forEach((product) => {
      if (product?.id) {
        productCache.set(product.id, product);
      }
    });
  }

  return productIds.map((id) => productCache.get(id)).filter(Boolean);
}

export async function getColorFamilyProducts(productGroupId, currentProductId) {
  if (!productGroupId || !currentProductId) {
    return [];
  }

  const [group] = await fetchProductGroups([productGroupId]);

  if (!group) {
    return [];
  }

  const buckets = new Map();

  collectGroupBuckets(group, buckets);

  const bucket = findColorFamilyBucket(buckets, currentProductId);

  if (!bucket) {
    return [];
  }

  return fetchProducts(bucket.productIds);
}
